import { type FeasibilityResponse, type Mutation, type StepResponse } from '@pkg/graphql/codegen/graphql';
import { prepareMutation } from '@pkg/graphql/mutations';
import executeStepMutation from '@pkg/graphql/mutations/executeStep.graphql';
import { ActivityStatusType } from '@pkg/types';

import { links, Request } from '@app/client';
import {
    type ApiResult,
    type BaseParams,
    type CallGetTokensListingActivityResponse,
    type CallGetTokensSalesActivityResponse,
    type DaasCollectionContractsResponse,
    type DaasCollectionMarketplaceResponse,
    type DaasCollectionTokensResponse,
    type EventParsedData,
    type GetTokenDetailsListResponse,
    type GetTokenOwnerCountResponse,
    type GraphqlParams,
} from '@app/client/common/structs';
import { setError } from '@app/store/actions/error';

import { apiFactory, apiReturnResults } from '../../ApiFactory';

import {
    type CollectionOwnersResponse,
    type FloorPriceChangesProps,
    type FloorPriceChangesResponse,
    type YacsTokensListProps,
} from './types';

export const getTokenDetailsList = (
    params: BaseParams & YacsTokensListProps,
): Promise<ApiResult<GetTokenDetailsListResponse>> => {
    const target = links.daas().tokens().list();
    const { tokens } = params;

    return Request.postRequest<GetTokenDetailsListResponse>({
        ...params,
        target,
        value: { tokens },
        apiSettingsType: 'daas',
    });
};
//TODO use v2 if can
export const callGetTokenDetailsList = (params: BaseParams & YacsTokensListProps) =>
    apiReturnResults<GetTokenDetailsListResponse>(getTokenDetailsList, params, true);

export const getFloorPriceChanges = (
    params: BaseParams & { customQuery: string },
): Promise<ApiResult<FloorPriceChangesResponse>> => {
    const target = links.daas().marketplace().floorPriceChanges;
    return Request.getRequest<FloorPriceChangesResponse>({
        ...params,
        target,
        apiSettingsType: 'daas',
    });
};

//TODO neaten slug array in call
export const callGetFloorPriceChanges = (params: BaseParams & FloorPriceChangesProps) => {
    const date = params.date.toISOString().split('T')[0];
    let querystring = `?dates=${date}`;

    if (params.slugs) {
        params.slugs.forEach((slug) => {
            querystring += `&slugs=${slug}`;
        });
    }

    if (params.contracts) {
        params.contracts.forEach((contractData) => {
            contractData.tokens.forEach((token) => {
                querystring += `&contracts=${contractData.contract}`;
                querystring += `&tokens=${token}`;
            });
        });
    }

    const customParams = { ...params, customQuery: querystring };
    return apiReturnResults<FloorPriceChangesResponse>(getFloorPriceChanges, customParams, true);
};

export const getCollectionOwners = (
    params: BaseParams & { contract: string },
): Promise<ApiResult<CollectionOwnersResponse>> => {
    const target = links.daas().marketplace().owners(params.contract);

    return Request.getRequest<CollectionOwnersResponse>({
        ...params,
        target,
        apiSettingsType: 'daas',
    });
};

export const callGetCollectionOwners = (params: BaseParams & { contract: string }) =>
    apiReturnResults<CollectionOwnersResponse>(getCollectionOwners, params);

export const getCollectionTokenList = (
    params: BaseParams & { slug: string },
): Promise<ApiResult<DaasCollectionTokensResponse>> => {
    const { slug } = params;
    const target = links.daas().tokens().collection(slug);

    return Request.getRequest<DaasCollectionTokensResponse>({
        ...params,
        target,
        apiSettingsType: 'daas',
    });
};

export const callGetCollectionTokenList = (
    params: BaseParams & { slug: string },
): Promise<DaasCollectionTokensResponse> => {
    return apiFactory()
        .call(getCollectionTokenList, { ...params }, { publicRequest: true })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }

            setError({
                error: result.error,
                log: true,
                source: 'Error getting token details',
            });

            return result.error;
        })
        .catch((e) => {
            setError({
                ...e,
                log: true,
                source: 'Error getting token details',
            });
            return e;
        });
};

export const getCollectionMetadata = (
    params: BaseParams & { slug: string },
): Promise<ApiResult<DaasCollectionMarketplaceResponse>> => {
    const { slug } = params;
    const target = links.daas().marketplace().collectionsBySlug(slug);

    return Request.getRequest<DaasCollectionMarketplaceResponse>({
        ...params,
        target,
        apiSettingsType: 'daas',
    });
};

export const callGetCollectionMetadata = (
    params: BaseParams & { slug: string },
): Promise<DaasCollectionMarketplaceResponse> => {
    return apiFactory()
        .call(getCollectionMetadata, { ...params }, { publicRequest: true })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage =
                result?.error?.detail && typeof result.error.detail === 'string'
                    ? result.error.detail
                    : `Error getting token details`;
            throw new Error(errorMessage);
        });
};

export const getCollectionContracts = (
    params: BaseParams & { slug: string },
): Promise<ApiResult<DaasCollectionContractsResponse>> => {
    const { slug } = params;
    const target = links.daas().marketplace().collections().contracts(slug);

    return Request.getRequest<DaasCollectionContractsResponse>({
        ...params,
        target,
        apiSettingsType: 'daas',
    });
};

export const callGetCollectionContracts = (
    params: BaseParams & { slug: string },
): Promise<DaasCollectionContractsResponse> => {
    return apiFactory()
        .call(getCollectionContracts, { ...params }, { publicRequest: true })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage =
                result?.error?.detail && typeof result.error.detail === 'string'
                    ? result.error.detail
                    : `Error getting token details`;
            throw new Error(errorMessage);
        });
};

export const getTokensListingsActivity = (
    params: BaseParams & YacsTokensListProps,
): Promise<ApiResult<CallGetTokensListingActivityResponse>> => {
    const target = links.daas().tokens().activity().listing;
    const { tokens } = params;

    return Request.postRequest<CallGetTokensListingActivityResponse>({
        ...params,
        target,
        value: { tokens },
        apiSettingsType: 'daas',
    });
};

export const callGetTokensListingsActivity = (params: BaseParams & YacsTokensListProps): Promise<EventParsedData[]> => {
    return apiFactory()
        .call(getTokensListingsActivity, { ...params }, { publicRequest: true })
        .then((result) => {
            if (result.ok) {
                return result.value.listings.map(
                    (listing) =>
                        ({
                            addressFrom: listing.lister,
                            addressTo: listing.lister,
                            contract_address: listing.contract_address,
                            date: listing.listing_date,
                            id: listing.token_id,
                            value: parseFloat(listing.wei_price),
                            status: ActivityStatusType.Listed,
                        }) as EventParsedData,
                );
            }
            const errorMessage =
                result?.error?.detail && typeof result.error.detail === 'string'
                    ? result.error.detail
                    : `Error getting token details`;
            throw new Error(errorMessage);
        });
};

export const getTokensSalesActivity = (
    params: BaseParams & YacsTokensListProps,
): Promise<ApiResult<CallGetTokensSalesActivityResponse>> => {
    const target = links.daas().tokens().activity().sales;
    const { tokens } = params;

    return Request.postRequest<CallGetTokensSalesActivityResponse>({
        ...params,
        target,
        value: { tokens },
        apiSettingsType: 'daas',
    });
};

export const callGetTokensSalesActivity = (params: BaseParams & YacsTokensListProps): Promise<EventParsedData[]> => {
    return apiFactory()
        .call(getTokensSalesActivity, { ...params }, { publicRequest: true })
        .then((result) => {
            if (result.ok) {
                return result.value.marketplace_sales.map(
                    (sale) =>
                        ({
                            addressFrom: sale.seller_address,
                            addressTo: sale.buyer_address,
                            contract_address: sale.contract_address,
                            date: sale.block_timestamp,
                            id: sale.token_id,
                            value: parseInt(sale.total_amount_wei),
                            status: ActivityStatusType.Sale,
                        }) as EventParsedData,
                );
            }
            const errorMessage =
                result?.error?.detail && typeof result.error.detail === 'string'
                    ? result.error.detail
                    : `Error getting token details`;
            throw new Error(errorMessage);
        });
};

export const getTokenOwnerCount = (
    params: BaseParams & { contract: string; tokenId: string },
): Promise<ApiResult<GetTokenOwnerCountResponse>> => {
    const { contract, tokenId } = params;
    const target = links.daas().tokens().ownersNumber(contract, tokenId);

    return Request.getRequest<GetTokenOwnerCountResponse>({
        ...params,
        target,
        apiSettingsType: 'daas',
    });
};

export const callGetTokenOwnerCount = (
    params: BaseParams & { contract: string; tokenId: string },
): Promise<GetTokenOwnerCountResponse> => {
    return apiFactory()
        .call(getTokenOwnerCount, { ...params }, { publicRequest: true })
        .then((result) => {
            if (result.ok) {
                return result.value;
            }
            const errorMessage =
                result?.error?.detail && typeof result.error.detail === 'string'
                    ? result.error.detail
                    : `Error getting token details`;
            throw new Error(errorMessage);
        });
};

export const getCollectionMetadataByToken = (
    params: BaseParams & { contract: string; tokenId: string },
): Promise<ApiResult<DaasCollectionMarketplaceResponse>> => {
    const { contract, tokenId } = params;
    const target = links.daas().marketplace().collectionsByTokenId(contract, tokenId);
    return Request.getRequest<DaasCollectionMarketplaceResponse>({
        ...params,
        target,
        apiSettingsType: 'daas',
    });
};

export const callGetCollectionMetadataByToken = (params: BaseParams & { contract: string; tokenId: string }) =>
    apiReturnResults<DaasCollectionMarketplaceResponse>(getCollectionMetadataByToken, params);

export async function prepareOrder(params: GraphqlParams): Promise<Mutation> {
    return Request.graphqlMutation({
        ...params,
        query: prepareMutation,
        apiSettingsType: 'marketplace',
    });
}

export const callPrepareOrder = (params): Promise<FeasibilityResponse> => {
    return apiFactory()
        .graphqlCall(prepareOrder, params)
        .then((response) => {
            if (!response) {
                throw new Error();
            }
            return response.prepare;
        })
        .catch((e) => {
            setError({
                ...e,
                log: true,
                source: 'Error preparing order.',
            })();
            return e;
        });
};

export async function executeStep(params: GraphqlParams): Promise<Mutation> {
    return Request.graphqlMutation({
        ...params,
        query: executeStepMutation,
        apiSettingsType: 'marketplace',
    });
}

export const callExecuteStep = (params): Promise<StepResponse> => {
    return apiFactory()
        .graphqlCall(executeStep, params)
        .then((response) => {
            if (!response) {
                throw new Error();
            }
            return response;
        })
        .catch((e) => {
            setError({
                ...e,
                log: true,
                source: 'Error executing new order step.',
            })();
            return e;
        });
};
